import { useState } from 'react'
import { Sparkles, Check, X, Edit3 } from 'lucide-react'
import { api } from '@/lib/api'

/**
 * Bar inline para revisar y publicar un borrador de respuesta IA a una Review.
 *
 * AI Act art. 50 — transparencia: el componente marca explícitamente "Borrador IA"
 * para que el operador humano que aprueba sepa que la respuesta no es manual.
 * El draft acaba publicado en la review pública firmado "— Equipo JR2" (no se
 * disfraza de IA en la respuesta visible, en línea con cómo replies humanas
 * habituales en GBP no se autoetiquetan; el rastro queda en ContactActivityLog
 * con actorType + sourceModule='bots').
 */

export interface AiReviewSuggestionLite {
  id: string
  draftText: string
  confidenceScore: string | number | null
  status: 'PENDING' | 'APPROVED' | 'EDITED_AND_SENT' | 'REJECTED' | 'AUTO_PUBLISHED'
  botProfile?: { name?: string; model?: string }
}

interface Props {
  reviewId: string
  suggestion: AiReviewSuggestionLite
  onChange?: () => void
}

export function AiReviewSuggestionInline({ reviewId, suggestion, onChange }: Props) {
  const [editing, setEditing] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const draft = editing ?? suggestion.draftText
  const confidence =
    suggestion.confidenceScore != null ? Number(suggestion.confidenceScore) : null
  const confidenceLabel = confidence == null ? '—' : `${Math.round(confidence * 100)}%`
  const confidenceColor =
    confidence == null
      ? 'text-ink-700/50'
      : confidence >= 0.8
        ? 'text-emerald-700'
        : confidence >= 0.6
          ? 'text-amber-700'
          : 'text-red-700'

  async function approve() {
    setBusy(true)
    setErr(null)
    try {
      const editedText = editing && editing.trim().length > 0 ? editing.trim() : undefined
      await api(`/v1/marketing/reputation/reviews/${reviewId}/suggestion/approve`, {
        method: 'POST',
        body: JSON.stringify({ editedText }),
      })
      setEditing(null)
      onChange?.()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function reject() {
    setBusy(true)
    setErr(null)
    try {
      await api(`/v1/marketing/reputation/reviews/${reviewId}/suggestion/reject`, {
        method: 'POST',
      })
      setEditing(null)
      onChange?.()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-3 border border-blueprint-200 bg-blueprint-50 rounded px-3 py-2">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="size-3.5 text-blueprint-700" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-blueprint-700">
          borrador IA · {suggestion.botProfile?.name ?? 'bot'}
        </span>
        <span className={['text-[10px] font-mono ml-auto', confidenceColor].join(' ')}>
          confidence {confidenceLabel}
          {suggestion.botProfile?.model ? ` · ${suggestion.botProfile.model}` : ''}
        </span>
      </div>

      {editing != null ? (
        <textarea
          value={editing}
          onChange={(e) => setEditing(e.target.value)}
          rows={4}
          className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
        />
      ) : (
        <div className="bg-white border border-ink-900/10 rounded px-3 py-2 text-sm whitespace-pre-wrap">
          {draft}
        </div>
      )}

      {err && (
        <div className="mt-2 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {err}
        </div>
      )}

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => void approve()}
          className="bp-btn-primary"
          disabled={busy || draft.trim().length === 0}
        >
          <Check className="size-3" />
          {editing != null ? 'Publicar editado' : 'Aceptar y publicar'}
        </button>
        {editing == null ? (
          <button
            type="button"
            onClick={() => setEditing(suggestion.draftText)}
            className="bp-btn"
            disabled={busy}
          >
            <Edit3 className="size-3" /> Editar
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(null)}
            className="bp-btn"
            disabled={busy}
          >
            Cancelar edición
          </button>
        )}
        <button
          type="button"
          onClick={() => void reject()}
          className="bp-btn text-red-700 ml-auto"
          disabled={busy}
        >
          <X className="size-3" /> Rechazar
        </button>
      </div>
    </div>
  )
}
